/**
 * Floating action button — apply for a loan or track an application from any
 * public page. Collapsed to a loan icon; expands on tap.
 */

import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

function LoanIcon({ className, open }: { className?: string; open?: boolean }) {
  if (open) {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        aria-hidden="true"
        className={className}
      >
        <path d="M6 6l12 12M18 6 6 18" />
      </svg>
    )
  }

  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6 10h.01M18 14h.01" />
    </svg>
  )
}

export function ApplyLoanFab() {
  const { t } = useTranslation()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!open) return
    function onPointerDown(e: PointerEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  return (
    <>
      {open && (
        <button
          type="button"
          aria-hidden="true"
          tabIndex={-1}
          className="apply-loan-fab-backdrop fixed inset-0 z-30 bg-stone-900/20"
          onClick={() => setOpen(false)}
        />
      )}

      <div ref={rootRef} className="apply-loan-fab fixed z-40 flex flex-col items-end gap-2">
        {open && (
          <div className="flex flex-col items-stretch gap-2">
            <Link
              to="/apply"
              className="apply-loan-fab-action tap-target rounded-full border border-stone-200 bg-white px-4 py-3 text-sm font-semibold text-stone-900 shadow-lg"
              onClick={() => setOpen(false)}
            >
              {t('landing.applyCta')}
            </Link>
            <Link
              to="/track"
              className="apply-loan-fab-action tap-target rounded-full border border-stone-200 bg-white px-4 py-3 text-sm font-semibold text-stone-900 shadow-lg"
              onClick={() => setOpen(false)}
            >
              {t('landing.trackCta')}
            </Link>
          </div>
        )}

        <button
          type="button"
          aria-expanded={open}
          aria-label={open ? t('common.close') : t('landing.fabOpen')}
          onClick={() => setOpen((v) => !v)}
          className="apply-loan-fab-button landing-cta-primary tap-target flex size-14 items-center justify-center rounded-full text-white shadow-lg"
        >
          <LoanIcon
            open={open}
            className="size-7 transition-transform duration-200"
          />
        </button>
      </div>
    </>
  )
}
